import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { Item } from "@instead/shared";
import { api } from "../lib/api.ts";
import { loadFilters } from "../lib/filters.ts";
import { todayTitle, useT } from "../lib/i18n.ts";
import { DealCard, useReorder } from "../components/DealCard.tsx";
import { Ic } from "../components/Icons.tsx";
import { Toast, useToast } from "../components/Ui.tsx";

/**
 * The deal (D-25): persistent until reshuffled or saved. View mode has no
 * Start buttons — titles open things. Edit mode: drag to reorder, redraw
 * one card, replace it from the shelf, remove, add (deals one in — round 5:
 * no menu). Save selection saves at once under today's date; the name is
 * edited on the set page. Closest fit shows one dismissable banner (round 3).
 */
export function Deal() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: dealData, isFetched } = useQuery({ queryKey: ["deal"], queryFn: api.deal.get });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const { data: catData } = useQuery({ queryKey: ["categories"], queryFn: api.categories.list });
  const deal = dealData?.deal ?? null;
  const [ids, setIds] = useState<string[] | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, say] = useToast();
  const current = ids ?? deal?.itemIds ?? [];
  const { data: items } = useQuery({ queryKey: ["items", "byIds", current.join(",")], queryFn: () => api.items.byIds(current), enabled: current.length > 0, placeholderData: keepPreviousData });
  const { move, handleProps, cardProps } = useReorder(current, setIds);
  const cats = catData?.categories ?? [];
  const max = Math.min(5, settings?.rest.maxCards ?? 5);

  useEffect(() => {
    if (isFetched && !deal) void nav({ to: "/rest" });
  }, [isFetched, deal, nav]);

  if (!deal) return null;

  // Every change lands in the query cache directly: a refetch alone would show
  // the old deal for a beat, then jump — it should feel like nothing reloaded.
  async function dealAgain() {
    const filters = loadFilters();
    const r = await api.picks.shuffle({ count: settings?.rest.defaultCards ?? 3, filters, includeDone: settings?.rest.includeDone ?? false });
    qc.setQueryData(["deal"], { deal: { pickId: r.pickId, itemIds: r.itemIds, filters, relaxed: r.relaxed, createdAt: new Date().toISOString() } });
    setIds(null);
  }

  async function redraw(id: string) {
    const r = await api.picks.shuffle({ count: 1, filters: deal!.filters, persist: false, excludeItemIds: current });
    if (!r.itemIds[0]) return;
    setIds(current.map((x) => (x === id ? r.itemIds[0]! : x)));
  }

  async function addOne() {
    const r = await api.picks.shuffle({ count: 1, filters: deal!.filters, persist: false, excludeItemIds: current });
    if (r.itemIds[0]) setIds([...current, r.itemIds[0]]);
  }

  /** Replace one card by hand: keep what's on screen, then pick on the shelf. */
  async function replace(id: string) {
    const saved = await api.deal.put(current);
    qc.setQueryData(["deal"], saved);
    setIds(null);
    await nav({ to: "/pick", search: { into: "deal", replace: id } as never });
  }

  async function done() {
    const saved = await api.deal.put(current);
    qc.setQueryData(["deal"], saved);
    setEditing(false);
    setIds(null);
  }

  async function dismissBanner() {
    qc.setQueryData(["deal"], { deal: { ...deal!, relaxed: false } });
    await api.deal.put(current, false);
  }

  async function saveSelection() {
    if (busy) return;
    setBusy(true);
    try {
      const sel = await api.selections.create({ title: todayTitle(), itemIds: current });
      await api.deal.clear();
      // leave first: an empty deal in the cache would send this screen home
      await nav({ to: "/set/$id", params: { id: sel.id } });
      qc.setQueryData(["deal"], { deal: null });
      await qc.invalidateQueries({ queryKey: ["selections"] });
    } finally {
      setBusy(false);
    }
  }

  const open = async (item: Item) => {
    if (deal?.pickId) await api.picks.choose(deal.pickId, item.id).catch(() => api.items.open(item.id));
    else await api.items.open(item.id);
    void qc.invalidateQueries({ queryKey: ["items"] });
  };

  return (
    <div className="rest-col">
      <div className="screen-head">
        <button className="icon-btn" aria-label={t("back")} onClick={() => nav({ to: "/rest" })}>{Ic.back()}</button>
        <h1 className="title" style={{ textAlign: "center" }}>{editing ? t("deal.editing") : t("deal.title")}</h1>
        {editing ? (
          <button className="btn btn-primary" style={{ minHeight: 36 }} onClick={done}>{t("done")}</button>
        ) : (
          <span style={{ display: "inline-flex", gap: "var(--sp-1)" }}>
            <button className="icon-btn" onClick={() => setEditing(true)}>{Ic.edit({ size: 14 })} {t("edit")}</button>
            <button className="icon-btn" onClick={dealAgain}>{Ic.refresh({ size: 14 })} {t("rest.dealAgain")}</button>
          </span>
        )}
      </div>

      {deal.relaxed && !editing && (
        <div className="banner" role="status">
          <span>{t("deal.closest")}</span>
          <button className="icon-btn" style={{ minWidth: 32, minHeight: 32 }} aria-label={t("done")} onClick={dismissBanner}>{Ic.x({ size: 14 })}</button>
        </div>
      )}

      {current.length === 0 && <p className="meta" style={{ textAlign: "center", padding: "var(--sp-6) 0" }}>{t("deal.nothing")}</p>}

      <div className="deal">
        {(items ?? []).map((item) => (
          <DealCard key={item.id} item={item} cats={cats} mode={editing ? "edit" : "view"} onOpen={open}
            onRedraw={() => redraw(item.id)} onReplace={() => replace(item.id)} onRemove={() => setIds(current.filter((x) => x !== item.id))}
            onMove={(d) => move(item.id, d)} handleProps={handleProps(item.id, item.title)} cardProps={cardProps(item.id)} />
        ))}
      </div>

      {editing && current.length < max && (
        <button type="button" className="add-tile" style={{ marginTop: "var(--sp-3)" }} onClick={addOne}>{Ic.plus({ size: 16 })} {t("deal.addCard")}</button>
      )}

      {current.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-4)" }}>
          <button className="btn btn-primary" style={{ width: 230 }} disabled={busy} onClick={saveSelection}>{t("deal.save")}</button>
        </div>
      )}
      <Toast msg={toast} />
    </div>
  );
}
